import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, CloudArrowUpIcon, PhotoIcon, FolderIcon } from '@heroicons/react/24/outline';
import { useDropzone } from 'react-dropzone';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { addPortfolioItem } from '../../store/slices/userSlice';
import { fetchCurrentUser } from '../../store/slices/authSlice';
import Button from '../common/Button';
import toast from 'react-hot-toast';
import TagSelector from '../common/TagSelector';
import useLockBodyScroll from '../../hooks/useLockBodyScroll';
import { compressImage } from '../../utils/compression';
import { userAPI } from '../../services/api';
import { PortfolioFolder } from '../../types';
import { FaInstagram } from 'react-icons/fa';
import LocationPicker from '../common/LocationPicker';
import { addUploadJob, updateUploadJob } from '../../store/slices/uploadSlice';

const InstagramIcon = FaInstagram as any;

interface AddPortfolioModalProps {
  onClose: () => void;
  onSuccess?: (newItem: any, rewardData?: any) => void;
  preSelectedFolderId?: string;
  onOpenSyncInstagram?: () => void;
}

const AddPortfolioModal: React.FC<AddPortfolioModalProps> = ({ onClose, onSuccess, preSelectedFolderId, onOpenSyncInstagram }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [location, setLocation] = useState('');
  const [coordinates, setCoordinates] = useState<{lat: number, lng: number} | null>(null);
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [folders, setFolders] = useState<PortfolioFolder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string>(preSelectedFolderId || '');
  
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  useLockBodyScroll();

  // Fetch folders for user
  useEffect(() => {
    const fetchFolders = async () => {
      try {
        const response = await userAPI.getOwnPortfolioFolders();
        if (response.data.success) {
          setFolders(response.data.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch folders:', error);
      }
    };

    fetchFolders();
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxFiles: 1,
    onDrop: async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        try {
            const compressedFile = await compressImage(file);
            setImage(compressedFile);
            const reader = new FileReader();
            reader.onload = () => setPreview(reader.result as string);
            reader.readAsDataURL(compressedFile);
            // console.log('📸 Image selected:', compressedFile.name);
        } catch (error) {
            console.error('Compression failed', error);
            setImage(file);
            const reader = new FileReader();
            reader.onload = () => setPreview(reader.result as string);
            reader.readAsDataURL(file);
        }
      }
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !image) {
      toast.error('Please provide a title and select an image');
      return;
    }

    const jobId = `portfolio-${Date.now()}`;
    
    // Create background job
    dispatch(addUploadJob({
      id: jobId,
      title: title.trim(),
      type: 'portfolio',
      status: 'uploading',
      progress: 0,
      thumbnailUrl: preview || undefined
    }));

    // Trigger async thunk (Do not await, run in bg)
    const formData = new FormData();
    formData.append('image', image);
    formData.append('title', title.trim());
    formData.append('description', description.trim());
    formData.append('tags', tags.join(','));
    if (selectedFolderId) {
      formData.append('folder_id', selectedFolderId);
    }
    if (location) {
      formData.append('location', location);
      if (coordinates) {
        formData.append('latitude', coordinates.lat.toString());
        formData.append('longitude', coordinates.lng.toString());
      }
    }

    dispatch(addPortfolioItem({
      formData,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          dispatch(updateUploadJob({ id: jobId, progress: percentCompleted }));
          if (percentCompleted === 100) {
            dispatch(updateUploadJob({ id: jobId, status: 'processing' }));
          }
        }
      }
    })).unwrap()
    .then((payload) => {
      dispatch(updateUploadJob({ id: jobId, status: 'completed' }));
      if (user?.id) dispatch(fetchCurrentUser(user.id));
      const rewardData = payload?.__reward_unlocked;
      onSuccess?.(payload, rewardData || undefined);
      // Reload the page once the background upload completes
      window.location.reload();
    })
    .catch((error) => {
      dispatch(updateUploadJob({ id: jobId, status: 'error', error: typeof error === 'string' ? error : 'Upload failed' }));
    });

    // Close the modal immediately so the user is not blocked!
    onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[80] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 z-10 shrink-0">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Add Portfolio Item</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Share your best work or sync directly from Instagram
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              disabled={loading}
            >
              <XMarkIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
            </button>
          </div>

          {/* Source Tabs */}
          {onOpenSyncInstagram && (
            <div className="grid grid-cols-2 border-b border-gray-200 dark:border-gray-800 bg-gray-100/80 dark:bg-gray-800/50 p-2 gap-2 shrink-0">
              <button
                type="button"
                className="py-2.5 px-4 rounded-xl text-xs md:text-sm font-semibold transition-all flex items-center justify-center space-x-2 bg-white dark:bg-gray-900 text-indigo-600 dark:text-indigo-400 shadow-sm border border-gray-200/60 dark:border-gray-700"
              >
                <CloudArrowUpIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                <span>Upload from Device</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenSyncInstagram();
                }}
                className="py-2.5 px-4 rounded-xl text-xs md:text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-pink-600 dark:hover:text-pink-400 hover:bg-white/60 dark:hover:bg-gray-900/60 transition-all flex items-center justify-center space-x-2"
              >
                <InstagramIcon className="w-4 h-4 text-pink-500" />
                <span>Sync from Instagram</span>
              </button>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">
            <form id="add-portfolio-form" onSubmit={handleSubmit} className="space-y-6">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Image *
                </label>
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-2xl p-4 md:p-8 text-center cursor-pointer transition-colors ${
                    isDragActive 
                      ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/10' 
                      : 'border-gray-300 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }`}
                >
                  <input {...getInputProps()} disabled={loading} />
                  
                  {preview ? (
                    <div className="space-y-4">
                      <img
                        src={preview}
                        alt="Preview"
                        className="mx-auto h-48 md:h-96 rounded-xl object-contain"
                      />
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <p className="font-medium text-gray-900 dark:text-white">{image?.name}</p>
                        <p>{image && (image.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <CloudArrowUpIcon className="h-16 w-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                      <p className="text-lg font-medium text-gray-600 dark:text-gray-300 mb-2">
                        {isDragActive ? 'Drop image here' : 'Upload your work'}
                      </p>
                      <p className="text-gray-500 dark:text-gray-400 mb-4">
                        Drag & drop an image here, or click to select
                      </p>
                      <div className="text-sm text-gray-400 dark:text-gray-500 space-y-1">
                        <p>Supported formats: PNG, JPG, WEBP</p>
                        <p>Maximum size: 50MB</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Folder Selection */}
              {folders.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Add to Folder (Optional)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FolderIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      value={selectedFolderId}
                      onChange={(e) => setSelectedFolderId(e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent text-gray-900 dark:text-white appearance-none transition-all"
                      disabled={loading}
                    >
                      <option value="">No Folder (Individual Item)</option>
                      {folders.map((folder) => (
                        <option key={folder.id} value={folder.id}>
                          {folder.name} ({folder.item_count} items)
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="Give your work a compelling title..."
                  required
                  disabled={loading}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="w-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent resize-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="Describe your work, the concept, techniques used, or the story behind it..."
                  disabled={loading}
                />
              </div>

              {/* Tags */}
              <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tags
                  </label>
                  <TagSelector
                      selectedTags={tags}
                      onChange={setTags}
                      placeholder="Select tags to help others discover your work..."
                      maxTags={10}
                  />
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Location (Optional)
                </label>
                <div className="relative z-50">
                  <LocationPicker
                    value={location}
                    onChange={(loc, coords) => {
                      setLocation(loc);
                      if (coords) setCoordinates(coords);
                    }}
                    placeholder="Where was this taken?"
                  />
                </div>
              </div>
            </form>
          </div>

          {/* Footer Actions */}
          <div className="p-4 md:p-6 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 shrink-0 flex flex-col space-y-3">
            {loading && uploadProgress > 0 && (
              <div className="w-full">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Uploading Image...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
            <div className="flex space-x-3 w-full">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="add-portfolio-form"
                variant="primary"
                loading={loading}
                disabled={!title.trim() || !image || loading}
                className="flex-1"
              >
                {loading ? (uploadProgress >= 100 ? 'Processing...' : 'Uploading...') : 'Add to Portfolio'}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddPortfolioModal;
